import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, CheckCircle, Sparkles } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface SnapshotFreshness {
  org_id: string;
  org_name: string;
  last_snapshot: string | null;
  users_with_snapshots: number;
  total_users: number;
  is_stale: boolean;
}

export default function AdminSystem() {
  const [freshness, setFreshness] = useState<SnapshotFreshness[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningBackfill, setRunningBackfill] = useState<string | null>(null);
  const [runningSummaryBackfill, setRunningSummaryBackfill] = useState<'fill' | 'regenerate' | null>(null);
  const [runningOriginalScoreBackfill, setRunningOriginalScoreBackfill] = useState(false);
  const [runningTagBackfill, setRunningTagBackfill] = useState(false);
  const [runningTitleBackfill, setRunningTitleBackfill] = useState(false);
  const [runningIdeaTitleBackfill, setRunningIdeaTitleBackfill] = useState(false);
  const [runningImprovementsBackfill, setRunningImprovementsBackfill] = useState(false);
  const [titleOrgId, setTitleOrgId] = useState<string>('');
  const [ideaTitleOrgId, setIdeaTitleOrgId] = useState<string>('');
  const [improvementsOrgId, setImprovementsOrgId] = useState<string>('');
  const [titleStats, setTitleStats] = useState<{
    lastRun?: { updated: number; failed: number; total: number };
  } | null>(null);
  const [ideaTitleStats, setIdeaTitleStats] = useState<{
    lastRun?: { updated_rows: number; total_ideas: number; failed: number; days_covered: number };
  } | null>(null);
  const [improvementsStats, setImprovementsStats] = useState<{
    lastRun?: { updated: number; errors: number; remaining: number; processed: number };
  } | null>(null);
  const [originalScoreStats, setOriginalScoreStats] = useState<{
    missing: number;
    lastRun?: { updated: number; errors: number; remaining: number };
  } | null>(null);
  const [summaryStats, setSummaryStats] = useState<{
    needsRegeneration?: number;
    lastRun?: { updated: number; remaining: number };
  } | null>(null);
  const [tagStats, setTagStats] = useState<{
    missing: number;
    lastRun?: { updated: number; remaining: number };
  } | null>(null);

  // Tool recommendations state
  const [runningToolAnalysis, setRunningToolAnalysis] = useState(false);
  const [toolRecommendations, setToolRecommendations] = useState<any[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [toolAnalysisStats, setToolAnalysisStats] = useState<{
    processed?: number; errors?: number; tool_distribution?: Record<string, number>; category_distribution?: Record<string, number>;
  } | null>(null);
  const [toolFilterTool, setToolFilterTool] = useState<string>('all');
  const [toolFilterCategory, setToolFilterCategory] = useState<string>('all');

  // Orphaned conversations state
  interface OrphanedConversation {
    id: string;
    user_id: string;
    final_prompt: string;
    completed_at: string;
    user_name?: string;
  }
  const [orphanedConversations, setOrphanedConversations] = useState<OrphanedConversation[]>([]);
  const [loadingOrphans, setLoadingOrphans] = useState(false);
  const [rescuingConversation, setRescuingConversation] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_snapshot_freshness_admin');
      if (error) throw error;
      setFreshness(data || []);
    } catch (error) {
      console.error('Error fetching snapshot freshness:', error);
      toast.error('Failed to load system data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOriginalScoreStats = async () => {
    const { count } = await supabase
      .from('fluency_scores')
      .select('id', { count: 'exact', head: true })
      .is('original_score_1_100', null);
    setOriginalScoreStats({ missing: count || 0 });
  };

  const fetchTagStats = async () => {
    // Count total scores with best_prompt (eligible for tags)
    const { count: totalScores } = await supabase
      .from('fluency_scores')
      .select('id', { count: 'exact', head: true })
      .not('best_prompt_extracted', 'is', null);

    // Count unique submissions that HAVE tags
    const { data: withTags } = await supabase
      .from('fluency_submission_tags')
      .select('submission_id');
    
    const uniqueSubmissionsWithTags = new Set(withTags?.map(t => t.submission_id) || []).size;
    
    // Missing = total eligible - those with tags
    const missing = Math.max(0, (totalScores || 0) - uniqueSubmissionsWithTags);
    setTagStats({ missing });
  };

  const fetchOrphanedConversations = async () => {
    setLoadingOrphans(true);
    try {
      const { data, error } = await supabase
        .from('builder_conversations')
        .select('id, user_id, final_prompt, completed_at')
        .eq('status', 'completed')
        .not('final_prompt', 'is', null)
        .is('submission_id', null)
        .order('completed_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Fetch user names for the orphaned conversations
      const userIds = [...new Set((data || []).map(c => c.user_id))];
      const { data: profiles } = userIds.length > 0
        ? await supabase.from('profiles').select('user_id, full_name').in('user_id', userIds)
        : { data: [] };

      const profileMap = new Map((profiles || []).map(p => [p.user_id, p.full_name]));

      setOrphanedConversations((data || []).map(c => ({
        ...c,
        final_prompt: c.final_prompt!,
        completed_at: c.completed_at!,
        user_name: profileMap.get(c.user_id) || 'Unknown',
      })));
    } catch (error) {
      console.error('Error fetching orphaned conversations:', error);
    } finally {
      setLoadingOrphans(false);
    }
  };

  const rescueConversation = async (conversationId: string) => {
    setRescuingConversation(conversationId);
    try {
      const { data, error } = await supabase.functions.invoke('rescue-builder-prompt', {
        body: { conversation_id: conversationId },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      toast.success(`Rescued! Score: ${data.overall_score}/100`);
      fetchOrphanedConversations();
    } catch (error) {
      console.error('Rescue failed:', error);
      toast.error('Failed to rescue conversation');
    } finally {
      setRescuingConversation(null);
    }
  };

  const fetchToolRecommendations = async () => {
    setLoadingRecommendations(true);
    try {
      const { data, error } = await supabase
        .from('tool_recommendations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;
      setToolRecommendations(data || []);
    } catch (error) {
      console.error('Error fetching tool recommendations:', error);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const runToolAnalysis = async () => {
    setRunningToolAnalysis(true);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-tool-recommendations-v6', {
        body: { limit: 25 },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      toast.success(`Analyzed ${data.processed} prompts (${data.errors} errors)`);
      setToolAnalysisStats(data);
      fetchToolRecommendations();
    } catch (error) {
      console.error('Tool analysis error:', error);
      toast.error('Tool analysis failed');
    } finally {
      setRunningToolAnalysis(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchOriginalScoreStats();
    fetchTagStats();
    fetchOrphanedConversations();
    fetchToolRecommendations();
  }, []);

  const runBackfill = async (orgId: string, orgName: string) => {
    setRunningBackfill(orgId);
    try {
      const { data, error } = await supabase.functions.invoke('backfill-fluency-snapshots', {
        body: { org_id: orgId },
      });
      
      if (error) throw error;
      
      toast.success(`Backfill complete for ${orgName}: ${data.updated || 0} users updated`);
      fetchData();
    } catch (error) {
      console.error('Backfill error:', error);
      toast.error(`Backfill failed for ${orgName}`);
    } finally {
      setRunningBackfill(null);
    }
  };

  const runAllBackfills = async () => {
    const staleOrgs = freshness.filter(f => f.is_stale);
    if (staleOrgs.length === 0) {
      toast.info('No stale organizations to backfill');
      return;
    }

    for (const org of staleOrgs) {
      await runBackfill(org.org_id, org.org_name);
    }
  };

  const runSummaryBackfill = async (regenerate = false) => {
    setRunningSummaryBackfill(regenerate ? 'regenerate' : 'fill');
    try {
      const { data, error } = await supabase.functions.invoke('backfill-prompt-summaries', {
        body: { limit: 50, regenerate },
      });
      
      if (error) throw error;
      
      const action = regenerate ? 'regenerated' : 'generated';
      toast.success(`Summary backfill: ${data.updated || 0} ${action}, ${data.remaining || 0} remaining`);
      setSummaryStats({
        needsRegeneration: data.remaining,
        lastRun: { updated: data.updated || 0, remaining: data.remaining || 0 }
      });
      console.log('Summary backfill details:', data);
    } catch (error) {
      console.error('Summary backfill error:', error);
      toast.error('Summary backfill failed');
    } finally {
      setRunningSummaryBackfill(null);
    }
  };

  const runOriginalScoreBackfill = async () => {
    setRunningOriginalScoreBackfill(true);
    try {
      const { data, error } = await supabase.functions.invoke('backfill-original-scores', {
        body: { limit: 25 },
      });
      
      if (error) throw error;
      
      toast.success(`Original score backfill: ${data.updated} scored, ${data.remaining} remaining`);
      setOriginalScoreStats({
        missing: data.remaining,
        lastRun: { updated: data.updated, errors: data.errors, remaining: data.remaining }
      });
      fetchOriginalScoreStats();
    } catch (error) {
      console.error('Original score backfill error:', error);
      toast.error('Original score backfill failed');
    } finally {
      setRunningOriginalScoreBackfill(false);
    }
  };

  const runTagBackfill = async () => {
    setRunningTagBackfill(true);
    try {
      const { data, error } = await supabase.functions.invoke('backfill-tags', {
        body: { limit: 100 },
      });
      
      if (error) throw error;
      
      toast.success(`Tag backfill: ${data.updated} tagged, ${data.remaining} remaining`);
      setTagStats({
        missing: data.remaining,
        lastRun: { updated: data.updated, remaining: data.remaining }
      });
      fetchTagStats();
    } catch (error) {
      console.error('Tag backfill error:', error);
      toast.error('Tag backfill failed');
    } finally {
      setRunningTagBackfill(false);
    }
  };

  const runTitleBackfill = async () => {
    if (!titleOrgId) {
      toast.error('Please select an organization');
      return;
    }
    setRunningTitleBackfill(true);
    try {
      const { data, error } = await supabase.functions.invoke('backfill-titles', {
        body: { org_id: titleOrgId },
      });
      
      if (error) throw error;
      
      toast.success(`Title backfill: ${data.updated} updated, ${data.failed} failed out of ${data.total}`);
      setTitleStats({
        lastRun: { updated: data.updated, failed: data.failed, total: data.total }
      });
    } catch (error) {
      console.error('Title backfill error:', error);
      toast.error('Title backfill failed');
    } finally {
      setRunningTitleBackfill(false);
    }
  };

  const runIdeaTitleBackfill = async () => {
    if (!ideaTitleOrgId) {
      toast.error('Please select an organization');
      return;
    }
    setRunningIdeaTitleBackfill(true);
    try {
      const { data, error } = await supabase.functions.invoke('backfill-titles', {
        body: { target: 'ideas', days: 3, org_id: ideaTitleOrgId },
      });
      
      if (error) throw error;
      
      toast.success(`Idea title backfill: ${data.total_ideas} ideas across ${data.days_covered} days, ${data.failed} failed`);
      setIdeaTitleStats({
        lastRun: { updated_rows: data.updated_rows, total_ideas: data.total_ideas, failed: data.failed, days_covered: data.days_covered }
      });
    } catch (error) {
      console.error('Idea title backfill error:', error);
      toast.error('Idea title backfill failed');
    } finally {
      setRunningIdeaTitleBackfill(false);
    }
  };

  const runImprovementsBackfill = async () => {
    if (!improvementsOrgId) {
      toast.error('Please select an organization');
      return;
    }
    setRunningImprovementsBackfill(true);
    try {
      const { data, error } = await supabase.functions.invoke('backfill-improvements', {
        body: { limit: 50, org_id: improvementsOrgId },
      });
      
      if (error) throw error;
      
      toast.success(`Improvements backfill: ${data.updated} updated, ${data.errors} errors, ${data.remaining} remaining`);
      setImprovementsStats({
        lastRun: { updated: data.updated, errors: data.errors, remaining: data.remaining, processed: data.processed }
      });
    } catch (error) {
      console.error('Improvements backfill error:', error);
      toast.error('Improvements backfill failed');
    } finally {
      setRunningImprovementsBackfill(false);
    }
  };

  const staleCount = freshness.filter(f => f.is_stale).length;
  const healthyCount = freshness.filter(f => !f.is_stale).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">System Health</h1>
          <p className="text-muted-foreground">Monitor snapshot freshness and trigger backfills</p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Organizations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{freshness.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Healthy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{healthyCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Stale (7+ days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{staleCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Snapshot Freshness Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Snapshot Freshness by Organization</CardTitle>
          {staleCount > 0 && (
            <Button variant="outline" size="sm" onClick={runAllBackfills}>
              Run All Stale Backfills ({staleCount})
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Last Snapshot</TableHead>
                <TableHead>Users with Snapshots</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : freshness.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No organizations found
                  </TableCell>
                </TableRow>
              ) : (
                freshness.map((org) => (
                  <TableRow key={org.org_id}>
                    <TableCell className="font-medium">{org.org_name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {org.last_snapshot 
                        ? formatDistanceToNow(new Date(org.last_snapshot), { addSuffix: true })
                        : 'Never'
                      }
                    </TableCell>
                    <TableCell>
                      {org.users_with_snapshots} / {org.total_users}
                    </TableCell>
                    <TableCell>
                      {org.is_stale ? (
                        <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Stale
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/30">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Healthy
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => runBackfill(org.org_id, org.org_name)}
                        disabled={runningBackfill === org.org_id}
                      >
                        {runningBackfill === org.org_id ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            Running...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Run Backfill
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Original Score Backfill Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Original Prompt Score Backfill</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Score original prompts for accurate improvement metrics
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={runOriginalScoreBackfill}
            disabled={runningOriginalScoreBackfill}
          >
            {runningOriginalScoreBackfill ? (
              <>
                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-1" />
                Run Backfill (25)
              </>
            )}
          </Button>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            <strong>{originalScoreStats?.missing || 0}</strong> scores missing original prompt scores.
          </p>
          {originalScoreStats?.lastRun && (
            <p className="text-green-600">
              Last run: {originalScoreStats.lastRun.updated} updated, {originalScoreStats.lastRun.remaining} remaining
            </p>
          )}
          <p className="text-xs">
            Processes 25 records per run. Run multiple times to complete all historical scores.
          </p>
        </CardContent>
      </Card>

      {/* Prompt Summary Backfill Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Prompt Summary Backfill</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Generate or regenerate prompt summaries for historical scores
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => runSummaryBackfill(false)}
              disabled={runningSummaryBackfill !== null}
            >
              {runningSummaryBackfill === 'fill' ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Fill Missing (50)
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => runSummaryBackfill(true)}
              disabled={runningSummaryBackfill !== null}
            >
              {runningSummaryBackfill === 'regenerate' ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Regenerate Old (50)
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          {summaryStats?.needsRegeneration !== undefined && (
            <p>
              <strong>{summaryStats.needsRegeneration}</strong> records need regeneration.
            </p>
          )}
          {summaryStats?.lastRun && (
            <p className="text-green-600">
              Last run: {summaryStats.lastRun.updated} updated, {summaryStats.lastRun.remaining} remaining
            </p>
          )}
          <p>
            <strong>Fill Missing:</strong> Adds summaries to records that don't have one.
          </p>
          <p>
            <strong>Regenerate Old:</strong> Updates summaries starting with "If you run this prompt..." with varied openings.
          </p>
        </CardContent>
      </Card>

      {/* Tag Backfill Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Tag Backfill</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Generate tags for submissions missing them
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={runTagBackfill}
            disabled={runningTagBackfill}
          >
            {runningTagBackfill ? (
              <>
                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-1" />
                Generate Tags (100)
              </>
            )}
          </Button>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            <strong>{tagStats?.missing || 0}</strong> submissions missing tags.
          </p>
          {tagStats?.lastRun && (
            <p className="text-green-600">
              Last run: {tagStats.lastRun.updated} tagged, {tagStats.lastRun.remaining} remaining
            </p>
          )}
          <p className="text-xs">
            Processes 25 records per run. Run multiple times to complete all historical submissions.
          </p>
        </CardContent>
      </Card>

      {/* Title Backfill Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Title Backfill</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Regenerate prompt titles to outcome-oriented format
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={titleOrgId} onValueChange={setTitleOrgId}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select org..." />
              </SelectTrigger>
              <SelectContent>
                {freshness.map(org => (
                  <SelectItem key={org.org_id} value={org.org_id}>{org.org_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={runTitleBackfill}
              disabled={runningTitleBackfill || !titleOrgId}
            >
              {runningTitleBackfill ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Run Title Backfill
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            Generates 6-10 word outcome-oriented titles starting with action verbs for the selected org.
          </p>
          {titleStats?.lastRun && (
            <p className="text-green-600">
              Last run: {titleStats.lastRun.updated} updated, {titleStats.lastRun.failed} failed out of {titleStats.lastRun.total} total
            </p>
          )}
          <p className="text-xs">
            Uses internal batching (5 at a time with delays). May take 30-60 seconds.
          </p>
        </CardContent>
      </Card>

      {/* Idea Title Backfill Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Idea Title Backfill</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Regenerate idea card titles to outcome-oriented format (last 3 days)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={ideaTitleOrgId} onValueChange={setIdeaTitleOrgId}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select org..." />
              </SelectTrigger>
              <SelectContent>
                {freshness.map(org => (
                  <SelectItem key={org.org_id} value={org.org_id}>{org.org_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={runIdeaTitleBackfill}
              disabled={runningIdeaTitleBackfill || !ideaTitleOrgId}
            >
              {runningIdeaTitleBackfill ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Run Idea Title Backfill
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            Updates titles on existing idea cards for the last 3 days to use action-verb, outcome-oriented format (6-10 words).
          </p>
          {ideaTitleStats?.lastRun && (
            <p className="text-green-600">
              Last run: {ideaTitleStats.lastRun.total_ideas} ideas across {ideaTitleStats.lastRun.days_covered} days, {ideaTitleStats.lastRun.failed} failed
            </p>
          )}
        </CardContent>
      </Card>

      {/* Improvements Backfill Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Improvements Backfill</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Generate improvement bullets for older scores missing them
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={improvementsOrgId} onValueChange={setImprovementsOrgId}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select org..." />
              </SelectTrigger>
              <SelectContent>
                {freshness.map(org => (
                  <SelectItem key={org.org_id} value={org.org_id}>{org.org_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={runImprovementsBackfill}
              disabled={runningImprovementsBackfill || !improvementsOrgId}
            >
              {runningImprovementsBackfill ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Run Backfill (50)
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            Compares original vs improved prompts and generates 3-6 improvement bullet points using AI.
          </p>
          {improvementsStats?.lastRun && (
            <p className="text-green-600">
              Last run: {improvementsStats.lastRun.updated} updated, {improvementsStats.lastRun.errors} errors, {improvementsStats.lastRun.remaining} remaining
            </p>
          )}
          <p className="text-xs">
            Processes 50 records per run in batches of 5. Run multiple times to complete all records.
          </p>
        </CardContent>
      </Card>

      {/* Orphaned Conversations Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Orphaned Builder Conversations</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Completed conversations where scoring failed — prompt saved but never submitted
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchOrphanedConversations} disabled={loadingOrphans}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loadingOrphans ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {orphanedConversations.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {loadingOrphans ? 'Loading...' : 'No orphaned conversations found ✓'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Prompt Preview</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orphanedConversations.map((conv) => (
                  <TableRow key={conv.id}>
                    <TableCell className="font-medium">{conv.user_name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDistanceToNow(new Date(conv.completed_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate text-muted-foreground text-xs">
                      {conv.final_prompt.slice(0, 100)}...
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => rescueConversation(conv.id)}
                        disabled={rescuingConversation === conv.id}
                      >
                        {rescuingConversation === conv.id ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            Rescuing...
                          </>
                        ) : (
                          'Rescue'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* AI Tool Recommendations Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              AI Tool Recommendations
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Analyze prompts to recommend the best AI tool/model for each task
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchToolRecommendations}
              disabled={loadingRecommendations}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loadingRecommendations ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={runToolAnalysis}
              disabled={runningToolAnalysis}
            >
              {runningToolAnalysis ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-1" />
                  Run Analysis (25)
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stats from last run */}
          {toolAnalysisStats && (
            <div className="text-sm text-muted-foreground space-y-1">
              <p className="text-green-600">
                Last run: {toolAnalysisStats.processed} analyzed, {toolAnalysisStats.errors} errors
              </p>
              {toolAnalysisStats.tool_distribution && Object.keys(toolAnalysisStats.tool_distribution).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(toolAnalysisStats.tool_distribution)
                    .sort(([, a], [, b]) => b - a)
                    .map(([tool, count]) => (
                      <Badge key={tool} variant="outline">
                        {tool}: {count}
                      </Badge>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* Summary of all recommendations */}
          {toolRecommendations.length > 0 && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm font-medium text-muted-foreground">Total: {toolRecommendations.length}</span>
                {(() => {
                  const toolCounts: Record<string, number> = {};
                  toolRecommendations.forEach(r => {
                    toolCounts[r.recommended_tool] = (toolCounts[r.recommended_tool] || 0) + 1;
                  });
                  return Object.entries(toolCounts)
                    .sort(([, a], [, b]) => b - a)
                    .map(([tool, count]) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}: {count} ({Math.round(100 * count / toolRecommendations.length)}%)
                      </Badge>
                    ));
                })()}
              </div>

              {/* Filters */}
              <div className="flex gap-2">
                <Select value={toolFilterTool} onValueChange={setToolFilterTool}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Filter tool..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tools</SelectItem>
                    {[...new Set(toolRecommendations.map(r => r.recommended_tool))].sort().map(tool => (
                      <SelectItem key={tool} value={tool}>{tool}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={toolFilterCategory} onValueChange={setToolFilterCategory}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Filter category..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {[...new Set(toolRecommendations.map(r => r.task_category))].sort().map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Results table */}
              <div className="max-h-[500px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Prompt</TableHead>
                      <TableHead>🥇 Best</TableHead>
                      <TableHead>🥈 Better</TableHead>
                      <TableHead>🥉 Good</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Complexity</TableHead>
                      <TableHead>Features</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {toolRecommendations
                      .filter(r => toolFilterTool === 'all' || r.recommended_tool === toolFilterTool || r.second_tool === toolFilterTool || r.third_tool === toolFilterTool)
                      .filter(r => toolFilterCategory === 'all' || r.task_category === toolFilterCategory)
                      .map((rec) => (
                        <TableRow key={rec.id}>
                          <TableCell className="text-xs text-muted-foreground max-w-[200px]">
                            <div className="truncate" title={rec.final_prompt}>
                              {rec.final_prompt?.slice(0, 80)}...
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <Badge variant="default" className="whitespace-nowrap">
                                {rec.recommended_tool}
                              </Badge>
                              <div className="text-[10px] text-muted-foreground">{rec.recommended_model || '—'}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {rec.second_tool ? (
                              <div className="space-y-1">
                                <Badge variant="secondary" className="whitespace-nowrap">
                                  {rec.second_tool}
                                </Badge>
                                <div className="text-[10px] text-muted-foreground">{rec.second_model || '—'}</div>
                              </div>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                          <TableCell>
                            {rec.third_tool ? (
                              <div className="space-y-1">
                                <Badge variant="outline" className="whitespace-nowrap">
                                  {rec.third_tool}
                                </Badge>
                                <div className="text-[10px] text-muted-foreground">{rec.third_model || '—'}</div>
                              </div>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {rec.task_category}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                rec.complexity_level === 'complex'
                                  ? 'border-destructive/50 text-destructive'
                                  : rec.complexity_level === 'moderate'
                                  ? 'border-amber-500/50 text-amber-700'
                                  : 'border-green-500/50 text-green-700'
                              }
                            >
                              {rec.complexity_level}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(rec.features_needed || []).map((f: string, i: number) => (
                                <Badge key={i} variant="secondary" className="text-[10px] px-1 py-0">
                                  {f}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {toolRecommendations.length === 0 && !loadingRecommendations && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No recommendations yet. Click "Run Analysis" to analyze prompts.
            </p>
          )}
          {loadingRecommendations && (
            <p className="text-sm text-muted-foreground text-center py-4">Loading...</p>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">About Snapshot Freshness</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            <strong>Stale</strong> means no new fluency snapshot has been created for any user in the organization in the last 7 days.
          </p>
          <p>
            <strong>Backfill</strong> re-computes fluency snapshots for users who have scoring activity but may be missing dimension data.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
